/* ============================================================================
 * GESKE - Newsletter 20% offer capture/reveal component
 * ----------------------------------------------------------------------------
 * <newsletter-offer-form> wraps an email capture form. On a valid submit it
 * subscribes the address to Klaviyo (client-side subscriptions API) and then
 * reveals the discount code. The code is never present in the markup before a
 * valid email is entered, so it cannot be read off the page pre-signup.
 *
 * Shared by every surface: homepage hero, collection band, PDP unlock card and
 * the site-wide announcement bar. Loaded once, site-wide, from theme.liquid.
 *
 * Data attributes on <newsletter-offer-form>:
 *   data-company-id  Klaviyo public API key (site id), e.g. S9P8SJ
 *   data-list-id     Klaviyo list id to subscribe to, e.g. UXgn6b
 *   data-source      custom_source label recorded against the signup
 * ========================================================================== */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var REVISION = '2025-07-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function NewsletterOfferForm() {
    return Reflect.construct(HTMLElement, [], NewsletterOfferForm);
  }
  NewsletterOfferForm.prototype = Object.create(HTMLElement.prototype);
  NewsletterOfferForm.prototype.constructor = NewsletterOfferForm;

  NewsletterOfferForm.prototype.connectedCallback = function () {
    if (this._init) return;
    this._init = true;

    this.form = this.querySelector('.nlo-form');
    this.input = this.querySelector('.nlo-input');
    this.error = this.querySelector('.nlo-error');
    this.success = this.querySelector('.nlo-success');
    this.submitBtn = this.querySelector('.nlo-submit');
    // Optional name fields (UK launch landing pages); absent on the classic
    // email-only surfaces, where behaviour is unchanged.
    this.firstInput = this.querySelector('.nlo-first');
    this.lastInput = this.querySelector('.nlo-last');

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    if (this.input) this.input.addEventListener('input', this.clearError.bind(this));

    var self = this;
    this.querySelectorAll('[data-copy-code]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { self.onCopy(e); });
    });
  };

  NewsletterOfferForm.prototype.clearError = function () {
    this.classList.remove('is-invalid');
    if (this.error) this.error.textContent = '';
    if (this.input) this.input.removeAttribute('aria-invalid');
  };

  NewsletterOfferForm.prototype.setLoading = function (on) {
    this.classList.toggle('is-loading', !!on);
    if (this.submitBtn) {
      this.submitBtn.disabled = !!on;
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    }
    if (this.input) this.input.disabled = !!on;
    if (this.firstInput) this.firstInput.disabled = !!on;
    if (this.lastInput) this.lastInput.disabled = !!on;
  };

  NewsletterOfferForm.prototype.onSubmit = function (e) {
    e.preventDefault();
    if (this.classList.contains('is-loading') || this.classList.contains('is-done')) return;

    var email = this.input ? (this.input.value || '').trim() : '';
    var firstName = this.firstInput ? (this.firstInput.value || '').trim() : '';
    if (this.firstInput && this.firstInput.required && !firstName) {
      this.classList.add('is-invalid');
      if (this.error) this.error.textContent = this.error.getAttribute('data-error-msg-name') || 'Please add your first name and email address.';
      this.firstInput.setAttribute('aria-invalid', 'true');
      this.firstInput.focus();
      return;
    }
    if (this.firstInput) this.firstInput.removeAttribute('aria-invalid');
    if (!EMAIL_RE.test(email)) {
      this.classList.add('is-invalid');
      if (this.error) this.error.textContent = this.error.getAttribute('data-error-msg') || 'Please enter a valid email address.';
      if (this.input) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
      }
      return;
    }
    this.clearError();
    this.setLoading(true);

    var self = this;
    this.subscribe(email)
      .catch(function (err) {
        // Never block the customer's code on a transient API hiccup; the address
        // was captured locally and the offer still stands.
        if (window.console) console.warn('[newsletter-offer] subscribe failed:', err);
      })
      .then(function () {
        self.setLoading(false);
        self.reveal(email);
      });
  };

  NewsletterOfferForm.prototype.subscribe = function (email) {
    var companyId = this.dataset.companyId;
    var listId = this.dataset.listId;
    if (!companyId || !listId) {
      return Promise.reject(new Error('Missing data-company-id or data-list-id'));
    }
    var profile = { type: 'profile', attributes: { email: email } };
    var firstName = this.firstInput ? (this.firstInput.value || '').trim() : '';
    var lastName = this.lastInput ? (this.lastInput.value || '').trim() : '';
    if (firstName) profile.attributes.first_name = firstName;
    if (lastName) profile.attributes.last_name = lastName;
    var attributes = { profile: { data: profile } };
    if (this.dataset.source) attributes.custom_source = this.dataset.source;

    var payload = {
      data: {
        type: 'subscription',
        attributes: attributes,
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };

    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok && res.status !== 202) {
        throw new Error('Klaviyo responded ' + res.status);
      }
      return res;
    });
  };

  NewsletterOfferForm.prototype.reveal = function (email) {
    this.classList.add('is-done');
    if (this.form) this.form.hidden = true;
    if (this.success) {
      this.success.hidden = false;
      var code = this.success.querySelector('[data-copy-code]');
      if (code && typeof code.focus === 'function') {
        try { code.focus({ preventScroll: true }); } catch (_) { code.focus(); }
      }
    }
    this.dispatchEvent(new CustomEvent('newsletter-offer:success', {
      bubbles: true,
      detail: {
        email: email,
        list: this.dataset.listId,
        firstName: this.firstInput ? (this.firstInput.value || '').trim() : ''
      }
    }));
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({ event: 'newsletter_offer_signup', list_id: this.dataset.listId });
    }
  };

  NewsletterOfferForm.prototype.onCopy = function (e) {
    var btn = e.currentTarget;
    var value = btn.getAttribute('data-copy-code') || '';
    var done = function () {
      var label = btn.querySelector('[data-copy-label]');
      if (label && !label._busy) {
        label._busy = true;
        var prev = label.textContent;
        label.textContent = 'Copied';
        btn.classList.add('is-copied');
        setTimeout(function () {
          label.textContent = prev;
          label._busy = false;
          btn.classList.remove('is-copied');
        }, 1500);
      }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(function () { fallbackCopy(value); done(); });
    } else {
      fallbackCopy(value);
      done();
    }
  };

  function fallbackCopy(value) {
    try {
      var ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (_) { /* no-op */ }
  }

  if ('customElements' in window && !customElements.get('newsletter-offer-form')) {
    customElements.define('newsletter-offer-form', NewsletterOfferForm);
  }
})();
